/**
 * C-21 指示・依頼（FR-121/122・D-69・05 §6.1）。
 *
 * 2つの宛先が1つの画面に同居する：
 * - **社内の担当者への指示**（発注・発送・その他）＝外出先から「あれ手配しといて」を残す。
 * - **運営への発注代行依頼（外注）**＝LP 卸向け 特長03「メーカーへの発注も、外注できる」。
 *
 * ★ **モバイル前提の代表画面**（D-68＝下部タブの1つ）。狭い側を先に作って、広い画面では
 *   同じカードが並ぶだけにする（表にしない＝本文が主役で、列に切り分ける情報が少ない）。
 * ★ **宛先の変更は作らない**（FR-121）＝取り消して出し直すのが正。宛先だけ差し替えられると
 *   「誰に頼んだつもりだったか」の履歴が消える。
 * ★ 運営宛の作成フォームには**職人の氏名・連絡先を書かない**注記を出す（D-63 の入口抑止）。
 */

import { Badge, Card, Chip, ConfirmDialog, Empty, ErrorNote, formatDateTime, PageHeader, Spinner } from "@kenzai/ui";
import { console_, type ConsoleMe, type WorkRequest } from "@kenzai/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { hintStyle, inputStyle, labelStyle } from "./formStyles";

const KIND_LABEL: Record<WorkRequest["kind"], string> = {
  procure: "発注",
  ship: "発送",
  other: "その他",
};

const STATUS_LABEL: Record<WorkRequest["status"], string> = {
  open: "未対応",
  in_progress: "対応中",
  done: "完了",
  cancelled: "取消",
};

function statusTone(s: WorkRequest["status"]): "ok" | "warn" | "danger" | "neutral" {
  if (s === "done") return "ok";
  if (s === "cancelled") return "danger";
  if (s === "in_progress") return "warn";
  return "neutral";
}

export function Requests({ me }: { me: ConsoleMe }) {
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  const [cancelling, setCancelling] = useState<WorkRequest | null>(null);

  const filter = params.get("filter") ?? "";
  const orderId = params.get("order_id");
  const qs = new URLSearchParams();
  if (filter === "mine") qs.set("mine", "true");
  if (filter === "created_by_me") qs.set("created_by_me", "true");
  if (filter === "open") {
    qs.append("status", "open");
    qs.append("status", "in_progress");
  }
  if (orderId) qs.set("order_id", orderId);

  const list = useQuery({
    queryKey: ["requests", qs.toString()],
    queryFn: () => console_.requests(qs.toString() ? `?${qs}` : ""),
  });

  const patch = useMutation({
    mutationFn: (v: { id: number; updated_at: string; status?: WorkRequest["status"]; done_ack?: boolean }) =>
      console_.patchRequest(v.id, { updated_at: v.updated_at, status: v.status, done_ack: v.done_ack }),
    onSuccess: () => {
      setError(null);
      setCancelling(null);
      void qc.invalidateQueries({ queryKey: ["requests"] });
    },
    onError: (e) => {
      // ★失敗はダイアログを**閉じてから**知らせる（ErrorNote は本文側に描くので、
      //   開いたままだと理由がモーダルの裏に回って「押しても何も起きない」に見える＝S7 の教訓）
      setCancelling(null);
      setError(e);
    },
  });

  return (
    <>
      <PageHeader
        title="発注管理"
        subtitle="社内の担当者への指示と、運営への発注代行の依頼をここで出せます。"
      />

      <NewRequestForm onError={setError} />

      <ErrorNote error={error} />

      <div style={{ display: "flex", gap: "var(--kz-space-2)", flexWrap: "wrap", margin: "var(--kz-space-4) 0" }}>
        {[
          { key: "", label: "すべて" },
          { key: "open", label: "未対応・対応中" },
          { key: "mine", label: "自分あて" },
          { key: "created_by_me", label: "自分が出した" },
        ].map((f) => {
          const on = filter === f.key;
          return (
            // 絞り込みは受注管理と**同じチップ**（M1.7・S3）＝画面ごとに違う形の絞り込みを作らない
            <Chip
              key={f.key || "all"}
              on={on}
              data-testid={`req-filter-${f.key || "all"}`}
              onClick={() => {
                const next = new URLSearchParams(params);
                if (f.key) next.set("filter", f.key);
                else next.delete("filter");
                setParams(next);
              }}
            >
              {f.label}
            </Chip>
          );
        })}
      </div>

      {list.isLoading ? (
        <Spinner />
      ) : list.isError ? (
        <ErrorNote error={list.error} />
      ) : (list.data?.items.length ?? 0) === 0 ? (
        <Empty
          title="発注の指示・依頼はありません"
          hint="上のフォームから、社内の担当者への指示や、運営への発注代行の依頼を出せます。"
        />
      ) : (
        <div style={{ display: "grid", gap: "var(--kz-space-3)" }}>
          {list.data!.items.map((r) => (
            <RequestCard
              key={r.id}
              row={r}
              me={me}
              busy={patch.isPending}
              onAdvance={(status) => patch.mutate({ id: r.id, updated_at: r.updated_at, status })}
              onAck={() => patch.mutate({ id: r.id, updated_at: r.updated_at, done_ack: true })}
              onCancel={() => setCancelling(r)}
            />
          ))}
        </div>
      )}

      {cancelling && (
        <ConfirmDialog
          title="この依頼を取り消しますか？"
          body={
            <>
              取り消すと、宛先の担当者・運営はこの依頼を進められなくなります。
              <br />
              {/* 宛先の変更は作らない（FR-121）＝出し直しが正しい道であることを言う */}
              <strong>宛先を変えたい場合は、取り消してから出し直してください。</strong>
            </>
          }
          confirmLabel="取り消す"
          tone="danger"
          busy={patch.isPending}
          onConfirm={() =>
            patch.mutate({ id: cancelling.id, updated_at: cancelling.updated_at, status: "cancelled" })
          }
          onCancel={() => setCancelling(null)}
        />
      )}
    </>
  );
}

function RequestCard({
  row: r,
  me,
  busy,
  onAdvance,
  onAck,
  onCancel,
}: {
  row: WorkRequest;
  me: ConsoleMe;
  busy: boolean;
  onAdvance: (s: WorkRequest["status"]) => void;
  onAck: () => void;
  onCancel: () => void;
}) {
  const isMine = r.assignee_type === "staff" && r.assignee_user_id === me.id;
  const isCreator = r.created_by_id === me.id;
  const isAdmin = me.role === "admin";
  const canProgress = r.assignee_type === "staff" && (isMine || isAdmin) && r.status !== "done" && r.status !== "cancelled";
  const canCancel = (isCreator || isAdmin) && r.status !== "done" && r.status !== "cancelled";
  const needsAck = r.status === "done" && isCreator && r.done_ack_at === null;

  return (
    <Card padding="0">
      <div data-testid={`request-${r.id}`} style={{ padding: "var(--kz-space-4)", display: "grid", gap: "var(--kz-space-2)" }}>
        <div style={{ display: "flex", gap: "var(--kz-space-2)", flexWrap: "wrap", alignItems: "center" }}>
          <Badge tone={statusTone(r.status)}>{STATUS_LABEL[r.status]}</Badge>
          <Badge>{KIND_LABEL[r.kind]}</Badge>
          <Badge tone={r.assignee_type === "operator" ? "warn" : "neutral"}>
            {r.assignee_type === "operator" ? "運営に依頼" : `${r.assignee_name ?? "担当者"} へ`}
          </Badge>
          {needsAck && <Badge tone="ok">完了しました</Badge>}
          <span
            className="kz-num"
            style={{ marginLeft: "auto", fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}
          >
            {formatDateTime(r.created_at)}
          </span>
        </div>

        {r.order_display_no && (
          <div style={{ fontSize: "var(--kz-font-size-sm)" }}>
            <Link to={`/orders/${r.order_id}`} className="kz-num" style={{ color: "var(--kz-color-primary)" }}>
              {r.order_display_no}
            </Link>
            {r.customer_name && <span style={{ color: "var(--kz-color-ink-muted)" }}>／{r.customer_name}</span>}
            {/* D-69⑧：取消済みの受注に紐づく依頼＝運営・担当者が気づけるように出す */}
            {r.order_status === "取消" && (
              <>
                {" "}
                <Badge tone="danger">対象の注文は取消済み</Badge>
              </>
            )}
          </div>
        )}

        <p style={{ margin: 0, whiteSpace: "pre-wrap", lineHeight: 1.8 }}>{r.body}</p>

        {r.reply_note && (
          <div
            style={{
              background: "var(--kz-color-surface-sunken)",
              borderRadius: "var(--kz-radius-sm)",
              padding: "var(--kz-space-3)",
            }}
          >
            <div style={{ ...hintStyle, marginBottom: "var(--kz-space-1)" }}>対応の記録</div>
            <div style={{ whiteSpace: "pre-wrap" }}>{r.reply_note}</div>
          </div>
        )}

        <div style={{ display: "flex", gap: "var(--kz-space-2)", flexWrap: "wrap" }}>
          {canProgress && r.status === "open" && (
            <button type="button" className="kz-btn" disabled={busy} data-testid={`req-start-${r.id}`} onClick={() => onAdvance("in_progress")}>
              対応中にする
            </button>
          )}
          {canProgress && (
            <button type="button" className="kz-btn" disabled={busy} data-testid={`req-done-${r.id}`} onClick={() => onAdvance("done")}>
              完了にする
            </button>
          )}
          {needsAck && (
            <button type="button" className="kz-btn kz-btn--primary" disabled={busy} data-testid={`req-ack-${r.id}`} onClick={onAck}>
              確認しました
            </button>
          )}
          {canCancel && (
            <button type="button" className="kz-btn" disabled={busy} data-testid={`req-cancel-${r.id}`} onClick={onCancel}>
              取り消す
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}

function NewRequestForm({ onError }: { onError: (e: unknown) => void }) {
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [assigneeType, setAssigneeType] = useState<"staff" | "operator">("operator");
  const [assigneeUserId, setAssigneeUserId] = useState<string>("");
  const [kind, setKind] = useState<WorkRequest["kind"]>("procure");
  const [body, setBody] = useState("");

  const orderId = params.get("order_id");
  const staff = useQuery({
    queryKey: ["staff"],
    queryFn: () => console_.staff(),
    enabled: open && assigneeType === "staff",
  });
  // 受注詳細から来たときは、品目・数量・希望日の要約を本文の下書きにする（05 §6.1 C-21）＝
  // 自由文だけだと運営が「何を何個」発注すればよいか特定できない。
  const order = useQuery({
    queryKey: ["order", orderId],
    queryFn: () => console_.order(Number(orderId)),
    enabled: open && !!orderId,
  });

  const create = useMutation({
    mutationFn: () =>
      console_.createRequest({
        order_id: orderId ? Number(orderId) : null,
        kind,
        assignee_type: assigneeType,
        assignee_user_id: assigneeType === "staff" && assigneeUserId ? Number(assigneeUserId) : null,
        body,
      }),
    onSuccess: () => {
      onError(null);
      setBody("");
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ["requests"] });
    },
    onError,
  });

  if (!open) {
    return (
      <button type="button" className="kz-btn kz-btn--primary" data-testid="req-new" onClick={() => setOpen(true)}>
        発注の指示・依頼を出す
      </button>
    );
  }

  const summary =
    order.data && !body
      ? [
          order.data.order.display_no,
          // 明細の要約＝自由文だけだと運営が「何を何個」発注すればよいか特定できない（05 §6.1 C-21）。
          // 品名は 11 §2 の平文突合キー＝D-63（職人の PII）とは別物。
          ...order.data.items.map((i) => `${i.raw_text ?? `商品#${i.product_id ?? ""}`} ${i.qty ?? ""}${i.unit ?? ""}`),
          order.data.order.desired_date ? `希望日 ${order.data.order.desired_date}` : "",
        ]
          .filter(Boolean)
          .join("\n")
      : "";

  return (
    <Card>
      <div style={{ display: "grid", gap: "var(--kz-space-3)", maxWidth: 640 }}>
        <label style={labelStyle}>
          宛先
          <select className="kz-select"
            data-testid="req-assignee-type"
            value={assigneeType}
            onChange={(e) => setAssigneeType(e.target.value as "staff" | "operator")}
            style={inputStyle}
          >
            <option value="operator">運営に依頼する（発注の代行）</option>
            <option value="staff">社内の担当者に指示する</option>
          </select>
        </label>

        {assigneeType === "staff" && (
          <label style={labelStyle}>
            担当者
            <select className="kz-select"
              data-testid="req-assignee"
              value={assigneeUserId}
              onChange={(e) => setAssigneeUserId(e.target.value)}
              style={inputStyle}
            >
              <option value="">選んでください</option>
              {(staff.data ?? [])
                .filter((u) => u.status === "active")
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
            </select>
          </label>
        )}

        <label style={labelStyle}>
          種別
          <select className="kz-select"
            data-testid="req-kind"
            value={kind}
            onChange={(e) => setKind(e.target.value as WorkRequest["kind"])}
            style={inputStyle}
          >
            <option value="procure">発注</option>
            <option value="ship">発送</option>
            <option value="other">その他</option>
          </select>
        </label>

        <label style={labelStyle}>
          内容
          <textarea
            data-testid="req-body"
            value={body || summary}
            onChange={(e) => setBody(e.target.value)}
            rows={6}
            maxLength={2000}
            placeholder="例）◯◯建材工業（担当 佐藤様・03-1234-5678）へ、石膏ボード 50 枚を明日着で発注してください。"
            style={{ ...inputStyle, minHeight: 132, lineHeight: 1.8 }}
          />
        </label>

        {assigneeType === "operator" && (
          <div style={{ ...hintStyle, color: "var(--kz-status-warn)" }}>
            {/* D-63 の入口抑止＝運営宛の本文に職人の個人情報を書かせない */}
            発注先（メーカー・商社）のご担当者名や電話番号は、運営が連絡するために必要ですのでそのままお書きください。
            <br />
            <strong>ご注文いただいた職人さまのお名前・連絡先は書かないでください。</strong>
          </div>
        )}

        <div style={{ display: "flex", gap: "var(--kz-space-2)", flexWrap: "wrap" }}>
          <button
            type="button"
            data-testid="req-submit"
            className="kz-btn kz-btn--primary"
            disabled={!(body || summary).trim() || create.isPending}
            onClick={() => {
              if (!body && summary) setBody(summary);
              create.mutate();
            }}
          >
            この内容で出す
          </button>
          <button type="button" className="kz-btn" onClick={() => setOpen(false)}>
            やめる
          </button>
        </div>
      </div>
    </Card>
  );
}
